import { AnalyticsChartType } from '@/modules/common/types';

export type AnalyticsChartResolvedValue = {
  dataPointId: string;
  title: string;
  value?: unknown | null;
  isStale?: boolean | null;
};

export type AnalyticsChartSource = {
  chartType?: AnalyticsChartType | null;
  spec: unknown;
  resolvedValues: AnalyticsChartResolvedValue[];
};

export type AnalyticsChartDataRow = {
  name: string;
  isStale: boolean;
  [key: string]: string | number | boolean;
};

export type AnalyticsChartData = {
  chartType: AnalyticsChartType;
  data: AnalyticsChartDataRow[];
  seriesKeys: string[];
  isStacked: boolean;
};

export type AnalyticsTableRow = {
  row: Record<string, unknown>;
  fallbackName: string;
};

const DEFAULT_VALUE_KEY = 'value';

function isAnalyticsRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function readAnalyticsRecord(value: unknown) {
  return isAnalyticsRecord(value) ? value : {};
}

export function readAnalyticsString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function readAnalyticsNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (isAnalyticsRecord(value)) {
    const nestedValue = value.value ?? value.amount ?? value.total;
    return nestedValue === undefined ? null : readAnalyticsNumber(nestedValue);
  }

  return null;
}

export function readAnalyticsPresentation(spec: unknown) {
  const presentation = readAnalyticsRecord(spec).presentation;
  return isAnalyticsRecord(presentation) ? presentation : {};
}

function readChartRecord(spec: unknown) {
  const chart = readAnalyticsRecord(spec).chart;
  return isAnalyticsRecord(chart) ? chart : {};
}

function isAnalyticsChartType(value: unknown): value is AnalyticsChartType {
  return (
    typeof value === 'string' &&
    Object.values(AnalyticsChartType).includes(value as AnalyticsChartType)
  );
}

function getChartType(component: AnalyticsChartSource) {
  if (isAnalyticsChartType(component.chartType)) {
    return component.chartType;
  }

  const type = readChartRecord(component.spec).type;
  return isAnalyticsChartType(type) ? type : AnalyticsChartType.BAR;
}

function readCategoryKey(chart: Record<string, unknown>) {
  return readAnalyticsString(chart.xKey) ?? readAnalyticsString(chart.categoryKey) ?? 'name';
}

function readSeriesKeys(chart: Record<string, unknown>) {
  const yKeys = chart.yKeys;
  if (Array.isArray(yKeys)) {
    const keys = yKeys.map(readAnalyticsString).filter((key): key is string => Boolean(key));
    if (keys.length > 0) {
      return keys;
    }
  }

  return [readAnalyticsString(chart.valueKey) ?? DEFAULT_VALUE_KEY];
}

function readDataBindingIds(spec: unknown) {
  const dataBindings = readAnalyticsRecord(spec).dataBindings;
  if (!Array.isArray(dataBindings)) {
    return [];
  }

  return dataBindings
    .map((binding) =>
      isAnalyticsRecord(binding) ? readAnalyticsString(binding.dataPointId) : null
    )
    .filter((dataPointId): dataPointId is string => Boolean(dataPointId));
}

function selectResolvedValues(component: AnalyticsChartSource) {
  const bindingIds = readDataBindingIds(component.spec);
  if (bindingIds.length === 0) {
    return component.resolvedValues;
  }

  const valuesById = new Map(
    component.resolvedValues.map((resolvedValue) => [resolvedValue.dataPointId, resolvedValue])
  );

  return bindingIds
    .map((dataPointId) => valuesById.get(dataPointId))
    .filter((resolvedValue): resolvedValue is AnalyticsChartResolvedValue =>
      Boolean(resolvedValue)
    );
}

function rowHasChartKeys(row: Record<string, unknown>, categoryKey: string, seriesKeys: string[]) {
  return categoryKey in row || seriesKeys.some((seriesKey) => seriesKey in row);
}

function unwrapChartValueEnvelope(value: unknown, categoryKey: string, seriesKeys: string[]) {
  if (!isAnalyticsRecord(value) || !('value' in value)) {
    return value;
  }

  const hasCategory = categoryKey in value;
  const hasNonDefaultSeriesKey = seriesKeys.some(
    (seriesKey) => seriesKey !== DEFAULT_VALUE_KEY && seriesKey in value
  );

  if (hasCategory || hasNonDefaultSeriesKey) {
    return value;
  }

  return value.value;
}

function unwrapTableValueEnvelope(value: unknown) {
  if (!isAnalyticsRecord(value) || !('value' in value)) {
    return value;
  }

  return value.value;
}

function readCategory(value: unknown, fallback: string) {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
}

function extractTableRows(value: unknown, categoryKey: string, seriesKeys: string[]) {
  const payload = unwrapChartValueEnvelope(value, categoryKey, seriesKeys);

  if (Array.isArray(payload)) {
    return payload
      .map((row, index) =>
        isAnalyticsRecord(row) ? { row, fallbackName: String(index + 1) } : null
      )
      .filter((item): item is { row: Record<string, unknown>; fallbackName: string } =>
        Boolean(item)
      );
  }

  if (!isAnalyticsRecord(payload)) {
    return [];
  }

  const rowEntries = Object.entries(payload).filter(
    (entry): entry is [string, Record<string, unknown>] => isAnalyticsRecord(entry[1])
  );
  const hasChartKeys = rowHasChartKeys(payload, categoryKey, seriesKeys);
  const hasNestedRows = rowEntries.some(([, row]) => rowHasChartKeys(row, categoryKey, seriesKeys));

  if (rowEntries.length > 0 && !hasChartKeys && hasNestedRows) {
    return rowEntries.map(([fallbackName, row]) => ({ row, fallbackName }));
  }

  return hasChartKeys ? [{ row: payload, fallbackName: '' }] : [];
}

export function extractAnalyticsTableRows(value: unknown): AnalyticsTableRow[] {
  const payload = unwrapTableValueEnvelope(value);

  if (Array.isArray(payload)) {
    return payload
      .map((row, index) =>
        isAnalyticsRecord(row) ? { row, fallbackName: String(index + 1) } : null
      )
      .filter((item): item is AnalyticsTableRow => Boolean(item));
  }

  if (!isAnalyticsRecord(payload)) {
    return [];
  }

  const rowEntries = Object.entries(payload).filter(
    (entry): entry is [string, Record<string, unknown>] => isAnalyticsRecord(entry[1])
  );

  if (rowEntries.length > 0) {
    return rowEntries.map(([fallbackName, row]) => ({ row, fallbackName }));
  }

  return Object.keys(payload).length > 0 ? [{ row: payload, fallbackName: '' }] : [];
}

function addAggregatedValue({
  rowsByName,
  usedSeriesKeys,
  name,
  seriesKey,
  value,
  isStale,
}: {
  rowsByName: Map<string, AnalyticsChartDataRow>;
  usedSeriesKeys: Set<string>;
  name: string;
  seriesKey: string;
  value: number;
  isStale: boolean;
}) {
  const existingRow = rowsByName.get(name) ?? { name, isStale: false };
  const currentValue = existingRow[seriesKey];
  const currentNumber = typeof currentValue === 'number' ? currentValue : 0;

  existingRow[seriesKey] = currentNumber + value;
  existingRow.isStale = existingRow.isStale || isStale;
  rowsByName.set(name, existingRow);
  usedSeriesKeys.add(seriesKey);
}

export function buildAnalyticsChartData(component: AnalyticsChartSource): AnalyticsChartData {
  const chart = readChartRecord(component.spec);
  const chartType = getChartType(component);
  const categoryKey = readCategoryKey(chart);
  const configuredSeriesKeys = readSeriesKeys(chart);
  const rowsByName = new Map<string, AnalyticsChartDataRow>();
  const usedSeriesKeys = new Set<string>();

  for (const resolvedValue of selectResolvedValues(component)) {
    const tableRows = extractTableRows(resolvedValue.value, categoryKey, configuredSeriesKeys);

    if (tableRows.length > 0) {
      for (const { row, fallbackName } of tableRows) {
        const name = readCategory(row[categoryKey], fallbackName || resolvedValue.title);

        for (const seriesKey of configuredSeriesKeys) {
          const value = readAnalyticsNumber(row[seriesKey]);
          if (value === null) {
            continue;
          }

          addAggregatedValue({
            rowsByName,
            usedSeriesKeys,
            name,
            seriesKey,
            value,
            isStale: Boolean(resolvedValue.isStale),
          });
        }
      }
      continue;
    }

    const value = readAnalyticsNumber(resolvedValue.value);
    if (value === null) {
      continue;
    }

    addAggregatedValue({
      rowsByName,
      usedSeriesKeys,
      name: resolvedValue.title,
      seriesKey: DEFAULT_VALUE_KEY,
      value,
      isStale: Boolean(resolvedValue.isStale),
    });
  }

  return {
    chartType,
    data: Array.from(rowsByName.values()),
    seriesKeys: Array.from(usedSeriesKeys),
    isStacked: chartType === AnalyticsChartType.STACKED_BAR || chart.stacked === true,
  };
}
